import React, { useEffect, useRef, useState } from "react"
import Breadcrumbs from "components/Common/Breadcrumb"
import { Card, CardBody, Col, Container, Row, Table } from "reactstrap"
import Filters from "components/Common/Filters"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import DownloadXLSX from "components/Shared/DownloadXLSX"
import TableShimmer from "components/Shared/TableShimmer"
import { pdfDownload } from "helpers/api_helper"
import { EXPORT_DATA } from "helpers/url_helper"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import Paginations from "components/Common/Paginations"
import { getPremiumGiftcardReports } from "store/actions/reportsAction"
import { getPremiumGiftCardList } from "store/actions/giftCardAction"
import { useTranslation } from "react-i18next"
import isPermissionEnabled from "common/isPermissionEnabled"

const PremiumGiftCardReports = () => {
  const { t, i18n: { language } } = useTranslation()
  document.title = t("Premium Gift Card Reports ")

  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page")) || 1
  const [isApiCalled, setIsApiCalled] = useState(false)

  const {
    loading,
    totalCount,
    totalPages,
    currentPage,
    itemsPerPage,
    premiumGiftcardReports,
  } = useSelector(state => state.Reports)
  const { premiumGiftCardList } = useSelector(state => state.GiftCard)
  const { userRole, accountType } = useSelector(state => state.Login)

  const [selectedFilters, setSelectedFilters] = useState({
    giftCardId: [],
  })
  const [dateRange, setDateRange] = useState({})
  const flatpickrRef = useRef(null)

  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      16,
      "view_premium_gift_card_reports",
      accountType
    )
    if (!isPermissionList) {
      navigate("/dashboard")
    }
  }, [userRole, accountType, navigate])

  useEffect(() => {
    dispatch(getPremiumGiftCardList())
  }, [dispatch])

  useEffect(() => {
    const giftCardIds = selectedFilters.giftCardId?.map(f => f.value) || []
    const payload = {
      perPage: itemsPerPage,
      pageNo: pageNumber,
      giftCardId: giftCardIds,
      dateRange: dateRange?.min && dateRange?.max
        ? { min: dateRange.min, max: dateRange.max }
        : {},
    }
    dispatch(getPremiumGiftcardReports(payload))
  }, [dispatch, pageNumber])

  const giftCardOptions = (premiumGiftCardList || []).map(item => ({
    label: language === "sa" ? item.nameAr || item.name : item.name || item.nameAr,
    value: item._id,
  }))

  const fetchPremiumGiftCardReports = () => {
    const giftCardIds = selectedFilters.giftCardId?.map(f => f.value) || []
    const payload = {
      perPage: itemsPerPage,
      pageNo: pageNumber,
      giftCardId: giftCardIds,
      dateRange: dateRange?.min && dateRange?.max
        ? { min: dateRange.min, max: dateRange.max }
        : {},
    }
    dispatch(getPremiumGiftcardReports(payload))
    setIsApiCalled(true)
  }

  const handleFilterChange = (selectedValue, filterName) => {
    setSelectedFilters(prev => ({
      ...prev,
      [filterName]: selectedValue,
    }))
  }

  const handleSearch = () => {
    const giftCardIds = selectedFilters.giftCardId?.map(f => f.value) || []
    const payload = {
      perPage: itemsPerPage,
      pageNo: 1,
      giftCardId: giftCardIds,
      dateRange: dateRange?.min && dateRange?.max
        ? { min: dateRange.min, max: dateRange.max }
        : {},
    }
    dispatch(getPremiumGiftcardReports(payload)).then(action => {
      const { statusCode, status } = action?.payload || {}
      if (statusCode === 200 && status) {
        navigate("/premium-gift-card-reports?page=1")
      }
    })
  }

  const handleReset = () => {
    const payload = {
      perPage: itemsPerPage,
      pageNo: 1,
      giftCardId: [],
      dateRange: {},
    }
    dispatch(getPremiumGiftcardReports(payload))
    navigate("/premium-gift-card-reports?page=1")
    setSelectedFilters({ giftCardId: [] })
    setDateRange({})
    if (flatpickrRef.current) {
      flatpickrRef.current.flatpickr.clear()
    }
  }

  const onPageChange = page => {
    const giftCardIds = selectedFilters.giftCardId?.map(f => f.value) || []
    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      giftCardId: giftCardIds,
      dateRange: dateRange?.min && dateRange?.max
        ? { min: dateRange.min, max: dateRange.max }
        : {},
    }
    dispatch(getPremiumGiftcardReports(payload))
    navigate(`/premium-gift-card-reports?page=${page}`)
  }

  const handleExportSummary = async () => {
    const giftCardIds = selectedFilters.giftCardId?.map(f => f.value) || []
    const payload = {
      type: "REPORT-PREMIUM-GIFT-CARD",
      exportType: "xlsx",
      giftCardId: giftCardIds,
      dateRange: dateRange?.min && dateRange?.max
        ? { min: dateRange.min, max: dateRange.max }
        : {},
    }
    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      DownloadXLSX(response, t("Premium Gift Card Reports - Summary"))
    }
  }

  const handleExportDetails = async () => {
    const giftCardIds = selectedFilters.giftCardId?.map(f => f.value) || []
    const payload = {
      type: "REPORT-PREMIUM-GIFT-CARD-DETAILS",
      exportType: "xlsx",
      giftCardId: giftCardIds,
      dateRange: dateRange?.min && dateRange?.max
        ? { min: dateRange.min, max: dateRange.max }
        : {},
    }
    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      DownloadXLSX(response, t("Premium Gift Card Reports - Details"))
    }
  }

  const filters = [
    {
      name: "giftCardId",
      type: "multiSelect",
      options: giftCardOptions,
      placeholder: t("Select Premium Gift Card"),
    },
  ]

  const columns = [
    "Name",
    "Amount",
    "Total Sent",
    "Total Amount",
    "Redeemed Count",
    "Redeemed Amount",
    "Remaining Count",
    "Remaining Amount",
  ]
  const columnKeys = [
    "name",
    "amount",
    "totalSent",
    "totalAmount",
    "redeemedCount",
    "redeemedAmount",
    "remainingCount",
    "remainingAmount",
  ]

  const formatAmount = value =>
    typeof value === "number" ? `${Number(value).toFixed(2)} SAR` : "-"

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <DataExportDropdown
                cardTitle={t("Premium Gift Card Reports")}
                isExportData={true}
                exportOptions={[
                  { labelKey: "Export Summary", onClick: handleExportSummary },
                  { labelKey: "Export Details", onClick: handleExportDetails },
                ]}
              />
              <CardBody>
                <Filters
                  filters={filters}
                  hideSearch={true}
                  handleFilterChange={handleFilterChange}
                  handleSearch={handleSearch}
                  onReset={handleReset}
                  selectedFilters={selectedFilters}
                  isRangeDate={true}
                  dateRange={dateRange}
                  flatpickrRef={flatpickrRef}
                  setDateRange={setDateRange}
                  colSize={4}
                />
                <div className="table-responsive">
                  {loading ? (
                    <TableShimmer row={7} col={7} />
                  ) : premiumGiftcardReports?.length === 0 ? (
                    <p className="text-center my-5 py-3">
                      {t("No premium gift card report found")}
                    </p>
                  ) : (
                    <Table className="table mb-0" hover>
                      <thead className="table-light full_th">
                        <tr>
                          {columns.map((col, idx) => (
                            <th key={idx}>{t(col)}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {premiumGiftcardReports?.map((item, idx) => (
                          <tr key={idx}>
                            <td>
                              {language === "sa" ? item.nameAr || item.name : item.name || item.nameAr}
                            </td>
                            <td>{formatAmount(item.amount)}</td>
                            {columnKeys.slice(2).map((key, colIdx) => (
                              <td key={colIdx}>
                                {typeof item[key] === "number"
                                  ? key.includes("Amount")
                                    ? formatAmount(item[key])
                                    : item[key]
                                  : item[key] ?? "-"}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </div>
                {!loading && premiumGiftcardReports?.length > 0 && (
                  <div className="mt-3">
                    <Paginations
                      totalCount={totalCount}
                      totalPages={totalPages}
                      currentPage={currentPage}
                      onPageChange={onPageChange}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default PremiumGiftCardReports
